/**
 * consolidate-integrations — assemble stackwright.integrations.yml from per-spec
 * api-config-*.json artifacts (swp-3l9j).
 *
 * Parallel api-otter fan-out leaves per-spec artifacts on disk; this tool
 * merges them atomically after all parallel invocations complete — eliminating
 * the MERGE_MODE read-modify-write race condition.
 *
 * Pattern mirrors list-specs.ts: exported handler for direct testing, MCP
 * registration at the bottom.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { readdirSync, readFileSync, writeFileSync, existsSync, mkdirSync } from 'fs';
import { lockSync } from 'proper-lockfile';
import { join, basename } from 'path';
import yaml from 'js-yaml';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ApiArtifact {
  version?: string;
  generatedBy?: string;
  entities?: unknown[];
  auth?: IntegrationAuth;
  baseUrl?: string;
  specPath?: string;
  /** Populated by api-otter in CONSOLIDATE_DEFERRED mode */
  integrationName?: string;
  mockUrl?: string;
  integrationSpec?: string;
  integrationEndpoints?: { include?: string[]; exclude?: string[] };
  integrationFormat?: string;
  skipped?: Array<{ spec: string; format: string; reason: string }>;
}

interface IntegrationAuth {
  type?: string;
  header?: string;
  envVar?: string;
  scopes?: string[];
}

interface IntegrationEntry {
  type: string;
  name: string;
  spec: string;
  mockUrl: string;
  baseUrl: string;
  auth: IntegrationAuth;
  endpoints?: { include?: string[]; exclude?: string[] };
}

interface ExistingIntegrationsFile {
  integrations?: IntegrationEntry[];
  [key: string]: unknown;
}

export interface ConsolidateResult {
  written: boolean;
  integrationCount: number;
  path?: string;
  reason?: string;
  dedupedCount?: number;
}

export interface ConsolidateInput {
  projectRoot: string;
}

// ---------------------------------------------------------------------------
// Fallback port assignment
// ---------------------------------------------------------------------------

// When mockUrl is absent from artifact (legacy), assign a deterministic port
// based on position in the sorted artifact list. Starts at 4011 (matches the
// default in stackwright_pro_emit_mock_ports).
const BASE_MOCK_PORT = 4011;

// ---------------------------------------------------------------------------
// Integration name derivation (mirrors list-specs deriveIntegrationName)
// ---------------------------------------------------------------------------

const STRIP_SUFFIXES = ['-openapi', '-asyncapi', '-api', '-spec'];

function deriveNameFromFilename(filename: string): string {
  // api-config-noaa-weather.json → noaa-weather
  const base = basename(filename, '.json').replace(/^api-config-/, '');
  for (const suffix of STRIP_SUFFIXES) {
    if (base.endsWith(suffix)) return base.slice(0, -suffix.length);
  }
  return base;
}

// ---------------------------------------------------------------------------
// Handler (exported for direct testing)
// ---------------------------------------------------------------------------

export function handleConsolidateIntegrations(input: ConsolidateInput): ConsolidateResult {
  const { projectRoot } = input;
  const artifactsDir = join(projectRoot, '.stackwright', 'artifacts');

  if (!existsSync(artifactsDir)) {
    return { written: false, integrationCount: 0, reason: 'no per-spec artifacts found' };
  }

  // Enumerate api-config-*.json files (the per-integration artifacts from fan-out)
  let entries: string[];
  try {
    entries = readdirSync(artifactsDir).filter(
      (f) => f.startsWith('api-config-') && f.endsWith('.json') && f !== 'api-config.json'
    );
  } catch {
    return { written: false, integrationCount: 0, reason: 'could not read artifacts directory' };
  }

  if (entries.length === 0) {
    return { written: false, integrationCount: 0, reason: 'no per-spec artifacts found' };
  }

  // Sort alphabetically — deterministic YAML output order
  entries.sort();

  // ── Build integration entries ──────────────────────────────────────────────
  const integrationsByName = new Map<string, IntegrationEntry>();

  entries.forEach((filename, index) => {
    let artifact: ApiArtifact = {};
    try {
      const raw = readFileSync(join(artifactsDir, filename), 'utf-8');
      artifact = JSON.parse(raw) as ApiArtifact;
    } catch {
      // Corrupt artifact — skip; surface name from filename so the caller can detect gaps
    }

    const name = artifact.integrationName ?? deriveNameFromFilename(filename);

    // Spec path: prefer artifact field, fall back to conventional pattern
    const spec = artifact.integrationSpec ?? artifact.specPath ?? `./specs/${name}.yaml`;

    // Mock URL: prefer artifact field (set by api-otter via emit_mock_ports),
    // fall back to deterministic port based on sorted index.
    const mockUrl = artifact.mockUrl ?? `http://localhost:${BASE_MOCK_PORT + index}`;

    const baseUrl = artifact.baseUrl ?? '';
    const auth: IntegrationAuth = artifact.auth ?? { type: 'none' };

    // Integration format: 'openapi' unless skipped array says otherwise
    const isAsyncApi = artifact.skipped?.some((s) => s.format === 'asyncapi') ?? false;
    const integrationType = artifact.integrationFormat ?? (isAsyncApi ? 'asyncapi' : 'openapi');

    const entry: IntegrationEntry = {
      type: integrationType,
      name,
      spec,
      mockUrl,
      baseUrl,
      auth,
    };

    if (artifact.integrationEndpoints) {
      entry.endpoints = artifact.integrationEndpoints;
    }

    // Last-write-wins for duplicate names (should be impossible in practice)
    integrationsByName.set(name, entry);
  });

  const integrations = Array.from(integrationsByName.values());
  const dedupedCount = entries.length - integrations.length;

  // ── Read existing file (preserve non-integrations top-level keys) ──────────
  const integrationsYmlPath = join(projectRoot, 'stackwright.integrations.yml');
  let existing: ExistingIntegrationsFile = {};
  if (existsSync(integrationsYmlPath)) {
    try {
      const raw = readFileSync(integrationsYmlPath, 'utf-8');
      existing = (yaml.load(raw) as ExistingIntegrationsFile) ?? {};
    } catch {
      // Corrupt YAML — start fresh rather than preserving broken content
      existing = {};
    }
  }

  // Merge: keep any non-integrations top-level keys (e.g. version, metadata)
  const merged: ExistingIntegrationsFile = { ...existing, integrations };

  // ── Atomic write ────────────────────────────────────────────────────────────
  // Ensure file exists so proper-lockfile can lock on it
  mkdirSync(join(projectRoot), { recursive: true });
  if (!existsSync(integrationsYmlPath)) {
    writeFileSync(integrationsYmlPath, '', 'utf-8');
  }

  const release = lockSync(integrationsYmlPath, {
    realpath: false,
    stale: 10_000,
  });
  try {
    const header = `# stackwright.integrations.yml — Auto-generated by stackwright_pro_consolidate_integrations\n`;
    const body = yaml.dump(merged, { lineWidth: 120, noRefs: true });
    writeFileSync(integrationsYmlPath, header + body, 'utf-8');
  } finally {
    release();
  }

  return {
    written: true,
    integrationCount: integrations.length,
    path: 'stackwright.integrations.yml',
    ...(dedupedCount > 0 ? { dedupedCount } : {}),
  };
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerConsolidateIntegrationsTool(server: McpServer): void {
  server.tool(
    'stackwright_pro_consolidate_integrations',
    [
      'Assemble stackwright.integrations.yml from per-spec api-config-*.json artifacts.',
      'Called by the foreman AFTER all parallel api-otter invocations complete (swp-3l9j).',
      'Reads .stackwright/artifacts/api-config-{name}.json for each spec, deduplicates by',
      'integration name (last-write-wins), preserves any non-integrations top-level keys in',
      'an existing stackwright.integrations.yml, and writes the merged result atomically.',
      'Returns { written, integrationCount, path } on success or { written: false, reason } if',
      'no per-spec artifacts are found.',
    ].join(' '),
    {
      projectRoot: z.string().describe('Absolute path to the project root directory'),
    },
    async ({ projectRoot }) => {
      try {
        const result = handleConsolidateIntegrations({ projectRoot });
        return {
          content: [{ type: 'text', text: JSON.stringify(result, null, 2) }],
        };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return {
          content: [
            {
              type: 'text',
              text: JSON.stringify({ written: false, error: message }),
            },
          ],
          isError: true,
        };
      }
    }
  );
}
